import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Link,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const { login, loginWithGoogle, user, sendEmailVerification, reloadUser, needsVerification, verificationEmailSent, register } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // If user is logged in and email is verified, redirect to home
    if (user?.firebaseUser?.emailVerified) {
      navigate('/');
    }
  }, [user, navigate]);

  const validatePassword = () => {
    if (password.length < 6) {
      throw new Error('Password must be at least 6 characters long');
    }
    if (isRegistering && password !== confirmPassword) {
      throw new Error('Passwords do not match');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      if (isRegistering && !acceptedTerms) {
        throw new Error('Please accept the terms and conditions to continue');
      }
      setLoading(true);
      validatePassword();

      if (isRegistering) {
        await register(email, password);
      } else {
        await login(email, password);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setError('');
      setLoading(true);
      await sendEmailVerification();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    try {
      setError('');
      setLoading(true);
      await reloadUser();
      if (user?.firebaseUser?.emailVerified) {
        navigate('/');
      } else {
        setError('Email not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      setLoading(true);
      await loginWithGoogle();
      navigate('/');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const containerStyles = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const paperStyles = {
    width: '100%',
    maxWidth: isMobile ? '100%' : '400px',
    p: isMobile ? 2 : 4,
    bgcolor: 'background.paper',
    borderRadius: 2,
    border: (theme) => `1px solid ${theme.palette.divider}`,
    boxShadow: isMobile ? 'none' : 3,
  };

  if (needsVerification) {
    return (
      <Box sx={containerStyles}>
        <Paper elevation={3} sx={paperStyles}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <EmailIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom color="text.primary">
              Verify Your Email
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Check your inbox for a verification link.
            </Typography>
            {verificationEmailSent && (
              <Alert severity="success" sx={{ mt: 1 }}>
                Verification email sent!
              </Alert>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={handleCheckVerification}
            disabled={loading}
            sx={{ mb: 1.5, minHeight: '40px' }}
          >
            {loading ? <CircularProgress size={20} /> : 'I\'ve Verified My Email'}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={handleResendVerification}
            disabled={loading}
            sx={{ minHeight: '40px' }}
          >
            Resend Verification Email
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={containerStyles}>
      <Paper elevation={3} sx={paperStyles}>
        <Typography 
          component="h1" 
          variant="h6"
          align="center" 
          sx={{ mb: 1 }}
          color="text.primary"
        >
          {isRegistering ? 'Create Account' : 'Welcome Back'}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          align="center" 
          sx={{ mb: 1.5 }}
        >
          {isRegistering
            ? 'Sign up to start creating'
            : 'Sign in to continue'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        <Button
          fullWidth
          variant="outlined"
          startIcon={<GoogleIcon />}
          onClick={handleGoogleLogin}
          disabled={loading}
          sx={{ 
            minHeight: '40px',
            borderColor: 'divider',
            mb: 1,
            '&:hover': {
              borderColor: 'primary.main',
            },
          }}
        >
          Continue with Google
        </Button>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.75rem', textAlign: 'center' }}>
          By continuing with Google, you agree to our{' '}
          <Link href="https://writellect.com/terms" target="_blank" color="primary">
            Terms and Conditions
          </Link>
        </Typography>

        <Divider sx={{ mb: 2 }}>OR</Divider>

        <form onSubmit={handleSubmit}>
          <TextField
            margin="dense"
            required
            fullWidth
            label="Email Address"
            autoComplete="email"
            autoFocus={!isMobile}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'divider',
                },
              },
            }}
          />
          <TextField
            margin="dense"
            required
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            autoComplete={isRegistering ? 'new-password' : 'current-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'divider',
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={togglePasswordVisibility}
                    edge="end"
                    color="primary"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {isRegistering && (
            <>
              <TextField
                margin="dense"
                required
                fullWidth
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'divider',
                    },
                  },
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    color="primary"
                    disabled={loading}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    I accept the{' '}
                    <Link href="https://writellect.com/terms" target="_blank" color="primary">
                      Terms and Conditions
                    </Link>
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
            </>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ 
              mt: 2, 
              mb: 1.5, 
              minHeight: '40px',
            }}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : isRegistering ? (
              'Sign Up'
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        <Box sx={{ mt: 1.5, textAlign: 'center' }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => setIsRegistering(!isRegistering)}
            sx={{ 
              cursor: 'pointer',
              color: 'primary.main',
              '&:hover': {
                color: 'primary.light',
              },
              padding: '4px',
              display: 'inline-block',
            }}
          >
            {isRegistering
              ? 'Already have an account? Sign in'
              : "Don't have an account? Sign up"}
          </Link>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login; 